import React, { useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaCog,
  FaDownload,
  FaExchangeAlt,
  FaFileAlt,
  FaGlobeAmericas,
  FaPlus,
  FaSearch,
  FaStore,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";
import "../styles/ReportingPage.css";

const METRICS = [
  { title: "Total Exports", value: "0", icon: <FaFileAlt />, meta: "Files generated" },
  { title: "Scheduled", value: "0", icon: <FaCog />, meta: "Automations active" },
  { title: "Storage Used", value: "0GB/5GB", icon: <FaCloudUploadAlt />, meta: "0% utilization", progress: 0 },
];

const CATEGORY_CARDS = [
  { title: "Merchant Reports", subtitle: "Settlement reconciliations & fee audits", lastRun: "—", avgSize: "0 MB", icon: <FaStore /> },
  { title: "Agent Reports", subtitle: "Commission tracking & performance", lastRun: "—", avgSize: "0 MB", icon: <FaUserTie /> },
  { title: "Transactions", subtitle: "Full transaction history & raw data", lastRun: "—", avgSize: "0 MB", icon: <FaExchangeAlt /> },
  { title: "Settlements", subtitle: "Payout cycles & bank transfers", lastRun: "—", avgSize: "0 MB", icon: <FaWallet /> },
];

const RECENT_ACTIVITY = [
  { name: "Q3_Settlement_Audit_Full", type: "Merchant", status: "Ready", date: "Oct 24, 14:20", action: <FaDownload /> },
  { name: "Daily_Tx_Batch_0029", type: "Transaction", status: "Processing", date: "Oct 24, 16:45", action: <FaCog /> },
  { name: "Agent_Comm_September", type: "Agent", status: "Failed", date: "Oct 23, 09:12", action: <FaExchangeAlt /> },
];

const SCHEDULED_JOBS = [
  { name: "Weekly Reconciliation", when: "Every Mon at 04:00", active: true },
  { name: "Monthly Agent Payouts", when: "1st day, 01:00", active: true },
  { name: "Daily Fraud Audit", when: "Daily at 23:59", active: false },
];

const TREND_BARS = [0, 0, 0, 0, 0, 0, 0];

const Analytics = ({ isSidebarOpen }) => {
  const [search, setSearch] = useState("");
  const [jobs, setJobs] = useState(SCHEDULED_JOBS);

  const filteredRows = useMemo(
    () =>
      RECENT_ACTIVITY.filter((row) =>
        `${row.name} ${row.type} ${row.status}`.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  const toggleJob = (index) => {
    setJobs((prev) => prev.map((job, i) => (i === index ? { ...job, active: !job.active } : job)));
  };

  return (
    <div className={`rp-page${isSidebarOpen ? " shifted" : ""}`}>
      <div className="rp-topbar">
        <div className="rp-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rp-metrics">
        {METRICS.map((item) => (
          <article className="rp-card rp-metric" key={item.title}>
            <div className="rp-metric__icon">{item.icon}</div>
            <div>
              <small>{item.title}</small>
              <strong>{item.value}</strong>
              {item.progress ? (
                <div className="rp-progress">
                  <span style={{ width: `${item.progress}%` }} />
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="rp-grid">
        <section className="rp-main">
          <div className="rp-section-head">
            <h3>Report Categories</h3>
          </div>

          <div className="rp-categories">
            {CATEGORY_CARDS.map((card) => (
              <article key={card.title} className="rp-card rp-category">
                <div className="rp-category__head">
                  <div>
                    <h4>{card.title}</h4>
                    <p>{card.subtitle}</p>
                  </div>
                  <span>{card.icon}</span>
                </div>
                <div className="rp-category__stats">
                  <div>
                    <small>Last Run</small>
                    <strong>{card.lastRun}</strong>
                  </div>
                  <div>
                    <small>Avg Size</small>
                    <strong>{card.avgSize}</strong>
                  </div>
                </div>
                <button type="button" className="rp-generate">
                  Generate Report
                </button>
              </article>
            ))}
          </div>

          <article className="rp-card rp-activity">
            <div className="rp-activity__head">
              <h3>Recent Activity</h3>
              <button type="button">View all</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td>{row.type}</td>
                    <td>
                      <span className={`rp-badge rp-badge--${row.status.toLowerCase()}`}>{row.status}</span>
                    </td>
                    <td>{row.date}</td>
                    <td>
                      <button type="button" className="rp-action-btn" title="Action">
                        {row.action}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>
        </section>

        <aside className="rp-side">
          <button type="button" className="rp-new-btn">
            <FaPlus /> New Report
          </button>

          <article className="rp-card rp-side-card">
            <h4>Volume Trends</h4>
            <div className="rp-trend-bars">
              {TREND_BARS.map((bar, idx) => (
                <span key={idx} style={{ height: `${bar}%` }} />
              ))}
            </div>
            <div className="rp-trend-meta">
              <small>Last 7 Days</small>
              <strong>0%</strong>
            </div>
          </article>

          <article className="rp-card rp-side-card">
            <h4>Scheduled Jobs</h4>
            <div className="rp-jobs">
              {jobs.map((job, idx) => (
                <div key={job.name} className="rp-job-row">
                  <div>
                    <strong>{job.name}</strong>
                    <small>{job.when}</small>
                  </div>
                  <button
                    type="button"
                    className={`rp-switch ${job.active ? "active" : ""}`}
                    onClick={() => toggleJob(idx)}
                    aria-label={`toggle ${job.name}`}
                  />
                </div>
              ))}
            </div>
            <button type="button" className="rp-outline-btn">
              Add New Schedule
            </button>
          </article>

          <article className="rp-card rp-map">
            <FaGlobeAmericas />
            <h5>Live Global Activity</h5>
            <p>Real-time sync active</p>
          </article>
        </aside>
      </div>
    </div>
  );
};

export default Analytics;
